import {
  getStepRailTile,
  type StepRailTile,
  stepRailConnectorColors,
  stepRailLabelColors,
} from '@zealot/ui/lib/stepRail';
import { cn } from '@zealot/ui/lib/utils';
import { cva } from 'class-variance-authority';
import { CheckIcon, CircleIcon, Loader2Icon } from 'lucide-react';
import { Fragment, type ReactNode } from 'react';
import { IconChip } from '../icon-chip';

// gloss
export type ProgressStepsStep = {
  id: string;
  label: string;
  icon?: ReactNode;
  description?: string;
};

// gloss
export type ProgressStepsProps = {
  steps: ProgressStepsStep[];
  currentStepId?: string | null;
  isComplete?: boolean;
  size?: 'default' | 'compact';
  'aria-label': string;
  className?: string;
};

type StepState = 'done' | 'in-flight' | 'pending';

// gloss
const STEP_CHIPS: Record<StepState, StepRailTile> = {
  done: getStepRailTile('done'),
  'in-flight': getStepRailTile('current'),
  pending: getStepRailTile('ahead'),
};

// gloss
const progressStepsConnectorVariants = cva('mt-6 h-1 min-w-6 flex-1 rounded-full', {
  variants: {
    state: {
      done: stepRailConnectorColors.filled,
      'in-flight': 'overflow-hidden bg-primary/20',
      pending: stepRailConnectorColors.pending,
    },
  },
  defaultVariants: { state: 'pending' },
});

// gloss
const getStepState = (index: number, currentIndex: number, isComplete: boolean): StepState => {
  if (isComplete) return 'done';
  if (currentIndex === -1) return 'pending';
  if (index < currentIndex) return 'done';
  return index === currentIndex ? 'in-flight' : 'pending';
};

const StepIcon = ({ step, state }: { step: ProgressStepsStep; state: StepState }) => {
  if (step.icon) return <>{step.icon}</>;
  if (state === 'done') return <CheckIcon className="size-6" />;
  return <CircleIcon className="size-2.5 fill-current" />;
};

// gloss
const getCompactStatus = (steps: ProgressStepsStep[], currentIndex: number, isComplete: boolean): string => {
  if (isComplete) return 'All steps done';
  const current = steps[currentIndex];
  return current ? `Step ${currentIndex + 1} of ${steps.length}: ${current.label}` : '';
};

const CompactProgressSteps = ({
  steps,
  currentIndex,
  isComplete,
  className,
  ariaLabel,
}: {
  steps: ProgressStepsStep[];
  currentIndex: number;
  isComplete: boolean;
  className?: string;
  ariaLabel: string;
}) => (
  <div
    role="group"
    aria-label={ariaLabel}
    data-slot="progress-steps"
    data-size="compact"
    className={cn('flex flex-wrap items-center gap-2', className)}
  >
    {steps.map((step, index) => {
      const state = getStepState(index, currentIndex, isComplete);
      return (
        <Fragment key={step.id}>
          {index > 0 && <span aria-hidden className="h-px w-4 bg-border" />}
          <span
            data-slot="progress-steps-step"
            data-state={state}
            aria-current={state === 'in-flight' ? 'step' : undefined}
            className={cn(
              'inline-flex items-center gap-1 text-xs',
              state === 'pending' ? 'text-muted-foreground' : 'text-foreground',
              state === 'in-flight' && 'font-medium',
            )}
          >
            {state === 'done' && <CheckIcon aria-hidden className="size-3 text-primary" />}
            {state === 'in-flight' &&
              (step.icon ?? <Loader2Icon aria-hidden className="size-3 animate-spin text-primary" />)}
            {state === 'pending' && <span aria-hidden className="size-2 rounded-full border border-muted-foreground" />}
            {step.label}
          </span>
        </Fragment>
      );
    })}
    <span role="status" aria-live="polite" data-slot="progress-steps-status" className="sr-only">
      {getCompactStatus(steps, currentIndex, isComplete)}
    </span>
  </div>
);

// gloss
const ProgressSteps = ({
  steps,
  currentStepId = null,
  isComplete = false,
  size = 'default',
  className,
  'aria-label': ariaLabel,
}: ProgressStepsProps) => {
  const currentIndex = currentStepId ? steps.findIndex((step) => step.id === currentStepId) : -1;
  const currentStep = currentIndex === -1 ? null : steps[currentIndex];

  if (size === 'compact') {
    return (
      <CompactProgressSteps
        steps={steps}
        currentIndex={currentIndex}
        isComplete={isComplete}
        className={className}
        ariaLabel={ariaLabel}
      />
    );
  }

  return (
    <div role="group" aria-label={ariaLabel} data-slot="progress-steps" className={cn('w-full', className)}>
      {steps.length > 0 && (
        <div role="list" data-slot="progress-steps-rail" className="flex items-start justify-center">
          {steps.map((step, index) => {
            const state = getStepState(index, currentIndex, isComplete);
            const connectorState = getStepState(index - 1, currentIndex, isComplete);
            return (
              <Fragment key={step.id}>
                {index > 0 && (
                  <div
                    aria-hidden
                    data-slot="progress-steps-connector"
                    data-state={connectorState}
                    className={progressStepsConnectorVariants({ state: connectorState })}
                  >
                    {connectorState === 'in-flight' && (
                      <span className="block h-full w-2/5 animate-progress-indeterminate rounded-full bg-primary" />
                    )}
                  </div>
                )}
                <div
                  role="listitem"
                  data-slot="progress-steps-step"
                  data-state={state}
                  aria-current={state === 'in-flight' ? 'step' : undefined}
                  className="flex w-20 shrink-0 flex-col items-center gap-2 sm:w-24"
                >
                  <div className="relative flex items-center justify-center">
                    {}
                    {state === 'in-flight' && (
                      <span
                        aria-hidden
                        data-slot="progress-steps-pulse"
                        className="absolute inset-0 animate-ping rounded-lg bg-primary/50 [animation-duration:1.6s]"
                      />
                    )}
                    <IconChip
                      size="2xl"
                      shape="square"
                      tone={STEP_CHIPS[state].tone}
                      fill={STEP_CHIPS[state].fill}
                      className="relative"
                    >
                      <StepIcon step={step} state={state} />
                    </IconChip>
                  </div>
                  <span
                    className={cn(
                      'text-center text-xs leading-tight',
                      state === 'pending' ? stepRailLabelColors.ahead : stepRailLabelColors.active,
                    )}
                  >
                    {step.label}
                  </span>
                </div>
              </Fragment>
            );
          })}
        </div>
      )}
      {currentStep && (
        <div data-slot="progress-steps-caption" aria-live="polite" className="mt-4 text-center">
          <p className="font-medium text-foreground text-sm">
            Step {currentIndex + 1} of {steps.length}: {currentStep.label}
          </p>
          {currentStep.description && <p className="mt-0.5 text-muted-foreground text-xs">{currentStep.description}</p>}
        </div>
      )}
    </div>
  );
};

export { ProgressSteps, progressStepsConnectorVariants };
